"use client";

import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown, RefreshCw } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { phCapture } from "@/utils/analytics";
import { quietClass } from "@/components/fantasy-props/chrome";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  deleteSavedView,
  listSavedViews,
  normalizeSavedViewSearch,
  renameSavedView,
  saveSavedView,
  type SavedView,
  type SavedViewToolKey,
} from "@/lib/saved-views";

type ToolToolbarProps = {
  title: ReactNode;
  hideTitle?: boolean;
  books?: ReactNode;
  leagues?: ReactNode;
  scope?: ReactNode;
  search?: ReactNode;
  levers?: ReactNode;
  minorLevers?: ReactNode;
  filters?: ReactNode;
  learn?: ReactNode;
  liveControl?: ReactNode;
  refreshButton?: ReactNode;
  overflow?: ReactNode;
  className?: string;
  rowClassName?: string;
  titleClassName?: string;
  material?: "chrome" | "bare";
  onFitChange?: (fit: ToolbarFit) => void;
  "aria-label"?: string;
};

export type ToolbarFit = Record<DemotableSlot, boolean>;

const DEMOTION_ORDER = ["search", "minorLevers", "levers", "learn", "overflow", "leagues", "scope"] as const;
type DemotableSlot = (typeof DEMOTION_ORDER)[number];

export const TOOLBAR_FULL_FIT: ToolbarFit = {
  search: true,
  minorLevers: true,
  levers: true,
  learn: true,
  overflow: true,
  leagues: true,
  scope: true,
};

function fitFromDemoted(order: readonly DemotableSlot[], count: number): ToolbarFit {
  const fit = { ...TOOLBAR_FULL_FIT };
  for (const slot of order.slice(0, count)) fit[slot] = false;
  return fit;
}

const SPACER_MIN_WIDTH = 8;

export type LiveRefreshCapsuleProps = {
  enabled: boolean;
  onToggle: () => void;
  onRefresh: () => void;
  refreshing?: boolean;
  refreshDisabled?: boolean;
  pausedAge?: string | null;
  stale?: boolean;
  refreshAriaLabel?: string;
  refreshTitle?: string;
  className?: string;
};

export type ToolViewsControlProps = {
  toolKey: SavedViewToolKey;
  userId?: string | null;
  currentSearch?: string;
  currentPath?: string;
  disabled?: boolean;
  tall?: boolean;
  contentClassName?: string;
  onApplySearch?: (search: string) => void;
};

function hasSlot(slot: ReactNode): boolean {
  return slot !== null && slot !== undefined && slot !== false;
}

function renderSlot(
  name: string,
  slot: ReactNode,
  className?: string,
  options: { shrink?: boolean } = {},
) {
  if (!hasSlot(slot)) return null;

  return (
    <div
      data-toolbar-slot={name}
      className={cn(
        "flex max-w-full flex-nowrap items-center gap-1.5",
        options.shrink ? "min-w-[5.5rem] shrink" : "shrink-0",
        className,
      )}
    >
      {slot}
    </div>
  );
}

type MeasureResult = { overflow: boolean; leftover: number; gap: number };

function measureRow(row: HTMLElement): MeasureResult | null {
  if (row.clientWidth === 0) return null;
  const spacer = row.querySelector<HTMLElement>('[data-toolbar-slot="spacer"]');
  const gap = Number.parseFloat(getComputedStyle(row).columnGap || "0") || 0;
  const last = row.lastElementChild;
  const overflow = last
    ? last.getBoundingClientRect().right > row.getBoundingClientRect().right + 1
    : false;
  return {
    overflow,
    leftover: (spacer?.getBoundingClientRect().width ?? 0) - SPACER_MIN_WIDTH,
    gap,
  };
}

const savedViewStorageError = "Couldn't save — storage unavailable.";

function getFallbackPathname(pathname: string | null): string {
  if (pathname) return pathname;
  if (typeof window !== "undefined") return window.location.pathname || "/";
  return "/";
}

export function ToolViewsControl({
  toolKey,
  userId,
  currentSearch,
  currentPath,
  disabled,
  tall = false,
  contentClassName,
  onApplySearch,
}: ToolViewsControlProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<SavedView[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [storageError, setStorageError] = useState<string | null>(null);

  const storageScope = useMemo(() => ({ userId, toolKey }), [userId, toolKey]);
  const refreshViews = useCallback(() => {
    setViews(listSavedViews(storageScope.userId, storageScope.toolKey));
  }, [storageScope]);

  useEffect(() => {
    refreshViews();
  }, [refreshViews]);

  const resolvedCurrentSearch = currentSearch ?? (typeof window !== "undefined" ? window.location.search : "");

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setStorageError(null);
      refreshViews();
    }
    if (!nextOpen) {
      setIsSaving(false);
      setNewViewName("");
      setEditingViewId(null);
      setEditingName("");
      setStorageError(null);
    }
  };

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStorageError(null);
    const view = saveSavedView(storageScope.userId, storageScope.toolKey, {
      name: newViewName,
      search: resolvedCurrentSearch,
      path: currentPath,
    });
    if (!view) {
      setStorageError(savedViewStorageError);
      return;
    }
    setViews((current) => [view, ...current]);
    phCapture("saved_view_saved", { tool: toolKey, view_count: views.length + 1 });
    setNewViewName("");
    setIsSaving(false);
  };

  const handleApply = (view: SavedView) => {
    const search = normalizeSavedViewSearch(view.search);
    const currentPathname = getFallbackPathname(pathname);
    const targetPath = view.path ?? currentPathname;
    phCapture("saved_view_applied", { tool: toolKey });
    if (targetPath === currentPathname && onApplySearch) {
      onApplySearch(search);
    } else {
      router.replace(`${targetPath}${search}`, { scroll: false });
    }
    setOpen(false);
  };

  const handleRename = (event: FormEvent<HTMLFormElement>, viewId: string) => {
    event.preventDefault();
    setStorageError(null);
    const renamed = renameSavedView(storageScope.userId, storageScope.toolKey, viewId, editingName);
    if (!renamed) {
      setStorageError(savedViewStorageError);
      return;
    }
    setViews((current) => current.map((view) => (view.id === viewId ? renamed : view)));
    setEditingViewId(null);
    setEditingName("");
  };

  const handleDelete = (viewId: string) => {
    setStorageError(null);
    if (deleteSavedView(storageScope.userId, storageScope.toolKey, viewId)) {
      setViews((current) => current.filter((view) => view.id !== viewId));
      phCapture("saved_view_deleted", {
        tool: toolKey,
        view_count: Math.max(views.length - 1, 0),
      });
      return;
    }
    setStorageError(savedViewStorageError);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(quietClass(open, tall), tall && "h-11", "disabled:cursor-not-allowed disabled:opacity-50")}
          aria-label="Open saved views"
        >
          <span>Saved views</span>
          {views.length > 0 ? (
            <span className="data-num text-[11px] font-semibold text-zinc-500">{views.length}</span>
          ) : null}
          <ChevronDown className="size-3.5 text-zinc-500" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn("glass-menu w-72 rounded-panel p-3 text-foreground", contentClassName)}>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Saved views
            </p>
            <button
              type="button"
              onClick={() => {
                setStorageError(null);
                setIsSaving((value) => !value);
              }}
              className="rounded px-1.5 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Save current view…
            </button>
          </div>

          {isSaving ? (
            <form onSubmit={handleSave} className="flex items-center gap-2">
              <input
                autoFocus
                value={newViewName}
                onChange={(event) => setNewViewName(event.target.value)}
                placeholder="View name"
                aria-label="Saved view name"
                className="h-9 min-w-0 flex-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              <button
                type="submit"
                className="h-9 rounded-md bg-primary px-2.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Save
              </button>
            </form>
          ) : null}

          {storageError ? (
            <p className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive" role="status">
              {storageError}
            </p>
          ) : null}

          {views.length === 0 ? (
            <p className="rounded-md border border-dashed border-white/[0.08] px-3 py-2 text-sm text-muted-foreground">
              Save your current filters as a view.
            </p>
          ) : (
            <ul className="max-h-72 space-y-1 overflow-y-auto pr-1 scrollbar-custom scrollbar-color-primary">
              {views.map((view) => (
                <li key={view.id} className="group rounded-md border border-transparent hover:border-white/[0.08] hover:bg-white/[0.03]">
                  {editingViewId === view.id ? (
                    <form onSubmit={(event) => handleRename(event, view.id)} className="flex items-center gap-2 p-1.5">
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        aria-label={`Rename ${view.name}`}
                        className="h-9 min-w-0 flex-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-2 text-sm text-foreground outline-none focus:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/40"
                      />
                      <button type="submit" className="rounded px-1.5 py-1 text-xs font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                        Save
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-2 p-1.5">
                      <button
                        type="button"
                        onClick={() => handleApply(view)}
                        className="min-w-0 flex-1 rounded px-1.5 py-1 text-left text-sm font-medium text-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                        title={view.search || "No query filters"}
                      >
                        <span className="block truncate">{view.name}</span>
                      </button>
                      <div className="flex shrink-0 items-center gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingViewId(view.id);
                            setEditingName(view.name);
                          }}
                          className="rounded px-1.5 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          aria-label={`Rename ${view.name}`}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(view.id)}
                          className="rounded px-1.5 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          aria-label={`Delete ${view.name}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ToolToolbar({
  title,
  hideTitle = true,
  books,
  leagues,
  scope,
  search,
  levers,
  minorLevers,
  filters,
  learn,
  liveControl,
  refreshButton,
  overflow,
  className,
  rowClassName,
  titleClassName,
  material = "chrome",
  onFitChange,
  "aria-label": ariaLabel = "Tool controls",
}: ToolToolbarProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const slots = { search, minorLevers, levers, learn, overflow, leagues, scope };
  const order = DEMOTION_ORDER.filter((slot) => hasSlot(slots[slot]));
  const orderKey = order.join(",");
  const [demotedState, setDemoted] = useState(0);
  const demoted = Math.min(demotedState, order.length);
  const fit = fitFromDemoted(order, demoted);
  const naturalWidths = useRef<Partial<Record<DemotableSlot, number>>>({});

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;

    const measure = () => {
      const result = measureRow(row);
      if (!result) return;
      for (const slot of order) {
        const el = row.querySelector<HTMLElement>(`[data-toolbar-slot="${slot}"]`);
        if (el) naturalWidths.current[slot] = el.getBoundingClientRect().width;
      }
      if (result.overflow) {
        if (demoted < order.length) setDemoted(demoted + 1);
        return;
      }
      if (demoted === 0) return;
      const candidate = order[demoted - 1];
      const need = naturalWidths.current[candidate] ?? 0;
      if (result.leftover >= need + result.gap) setDemoted(demoted - 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    for (const child of row.children) observer.observe(child);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoted, orderKey]);

  useEffect(() => {
    onFitChange?.(fit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFitChange, demoted, orderKey]);

  const titleNode =
    typeof title === "string" ? (
      <h1
        className={cn(
          hideTitle
            ? "sr-only"
            : "truncate whitespace-nowrap text-base font-semibold tracking-tight text-foreground sm:text-xl",
        )}
      >
        {title}
      </h1>
    ) : (
      title
    );

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      data-search-fits={fit.search ? "true" : "false"}
      data-toolbar-demoted={demoted}
      className={cn(
        "flex shrink-0 items-center",
        material === "chrome"
          ? "command-strip tool-command-strip min-h-14 px-3 py-2 sm:px-4"
          : "px-0",
        className,
      )}
    >
      <div
        ref={rowRef}
        className={cn(
          "flex h-9 w-full min-w-0 flex-nowrap items-center gap-x-1.5 overflow-hidden",
          rowClassName,
        )}
      >
        <div
          data-toolbar-slot="title"
          className={cn(hideTitle ? "sr-only" : "flex h-9 min-w-0 shrink items-center", titleClassName)}
        >
          {titleNode}
        </div>

        {renderSlot("books", books)}
        {fit.leagues ? renderSlot("leagues", leagues) : null}
        {fit.scope
          ? renderSlot("scope", scope, undefined, { shrink: order[demoted] === "scope" })
          : null}
        {fit.search ? renderSlot("search", search) : null}
        {fit.levers ? renderSlot("levers", levers) : null}
        {fit.minorLevers ? renderSlot("minorLevers", minorLevers) : null}

        <div data-toolbar-slot="spacer" className="min-w-2 flex-1" />

        <div className="ml-auto flex max-w-full min-w-0 shrink-0 flex-nowrap items-center justify-end gap-1.5">
          {fit.overflow ? renderSlot("overflow", overflow) : null}
          {fit.learn ? renderSlot("learn", learn) : null}
          {renderSlot("live", liveControl)}
          {renderSlot("refresh", refreshButton)}
          {renderSlot("filters", filters)}
        </div>
      </div>
    </div>
  );
}

export function LiveRefreshCapsule({
  enabled,
  onToggle,
  onRefresh,
  refreshing,
  refreshDisabled,
  pausedAge,
  stale,
  refreshAriaLabel = "Refresh",
  refreshTitle,
  className,
}: LiveRefreshCapsuleProps) {
  const isStale = !enabled && !!stale;

  return (
    <div
      role="group"
      aria-label="Live updates"
      className={cn(
        "flex h-9 shrink-0 items-stretch rounded-lg border transition-colors duration-150",
        enabled
          ? "border-primary/25 bg-primary/[0.08] shadow-sm shadow-black/20"
          : isStale
            ? "border-amber-400/25 bg-amber-500/[0.07] shadow-sm shadow-black/20"
            : "border-transparent bg-transparent hover:border-white/[0.14] hover:bg-white/[0.06]",
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={enabled}
        aria-label={
          enabled
            ? "Pause auto-refresh"
            : `Auto-refresh paused${pausedAge ? ` — snapshot ${pausedAge} old` : ""}${isStale ? ", data is stale" : ""}. Resume auto-refresh.`
        }
        className={cn(
          "relative inline-flex items-center gap-1.5 rounded-l-[7px] px-2.5 text-[11px] font-semibold before:absolute before:-inset-y-1 before:-left-1 before:content-['']",
          enabled ? "rounded-r-[7px] before:-right-1" : "before:right-0",
          "transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          enabled
            ? "text-primary hover:bg-primary/[0.06]"
            : isStale
              ? "text-amber-300/90 hover:bg-amber-500/[0.10]"
              : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground",
        )}
      >
        {enabled ? (
          <>
            <span className="relative flex size-2" aria-hidden="true">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-primary" />
            </span>
            <span className="hidden sm:inline">Live</span>
          </>
        ) : (
          <>
            <span
              className={cn(
                "size-1.5 shrink-0 rounded-full",
                isStale ? "animate-pulse bg-amber-400" : "bg-muted-foreground/60",
              )}
              aria-hidden="true"
            />
            {pausedAge ? (
              <span className="hidden tabular-nums min-[360px]:inline" aria-hidden="true">
                {pausedAge}
              </span>
            ) : null}
          </>
        )}
      </button>
      {!enabled && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing || refreshDisabled}
          aria-label={refreshAriaLabel}
          title={refreshTitle}
          className={cn(
            "relative inline-flex w-9 items-center justify-center rounded-r-[7px] border-l text-primary before:absolute before:-inset-y-1 before:left-0 before:-right-1 before:content-['']",
            "transition-colors duration-150 hover:bg-primary/[0.10]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            isStale ? "border-amber-400/25" : "border-white/[0.08]",
          )}
        >
          <RefreshCw className={cn("size-4", refreshing && "animate-spin")} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
